import { SourceType, SubscriptionPlanKey } from "@authory-packages/core";
import { getPlanTierLabel } from "../../utils/getPlanTierLabel";
import { AddSourceWizardStage } from "./AddSource/AddSource";
import { ManualImportPathTaken } from "./UniversalAddContentTracking";
import { AddContentWizardStage } from "./AddContent/AddContent";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { LearnMoreCTA } from "../ReusableStyledComponents/LearnMoreCTA";
import { FileUploadTarget, UnifiedAddContentStages } from "./AddContent/AddContent.types";
import { formatMMSS } from "../../utils/getUsageinMinutes";

export const getModalUACTTitle = (
    mainModalStage: UnifiedAddContentStages,
    sourceStage: AddSourceWizardStage,
    sourceType: SourceType,
    importPathTaken: ManualImportPathTaken,
    fileUploadTarget: FileUploadTarget,
    addContentStage: AddContentWizardStage,
    isProcessingItem?: boolean,
    isCheckingDuplicate?: boolean
) => {
    switch (mainModalStage) {
        case UnifiedAddContentStages.ChooseType:
            return "What type of content would you like to add?";
        case UnifiedAddContentStages.ArticleOptions:
            return "Let's add written articles to your account";
        case UnifiedAddContentStages.PodcastOptions:
            return "Let's add podcasts & audios to your account";
        case UnifiedAddContentStages.PodcastEpisodeOptions:
            return "Where are your podcasts hosted?";
        case UnifiedAddContentStages.VideoOptions:
            return "Let's add videos to your account";
        case UnifiedAddContentStages.LinkedInOptions:
            return "How should we add your LinkedIn content?";
        case UnifiedAddContentStages.PDFOptions:
            return "Let's add PDFs to your account";
        case UnifiedAddContentStages.ScreenshotOptions:
            return "Let's add some content to your account";
        case UnifiedAddContentStages.YouTubeOptions:
            return "How should we add your videos?";
        case UnifiedAddContentStages.Spotify:
            return "Spotify's a bit tricky at the moment...";
        case UnifiedAddContentStages.Image:
            return "Let's upload your images";
        case UnifiedAddContentStages.CollectionEditRules:
            return `Auto-update settings`;
        case UnifiedAddContentStages.CollectionChooseOptions:
            return `Add work samples to this collection`;
    }

    if (mainModalStage === UnifiedAddContentStages.AddSource) {
        if (sourceStage === AddSourceWizardStage.AddURL) {
            switch (sourceType) {
                case SourceType.Web:
                    return "Import all your articles from a website";
                case SourceType.PodcastRssFeed:
                    return "Add a podcast";
                case SourceType.Youtube:
                    return "Add a YouTube channel, profile or playlist";
                case SourceType.Linkedin:
                    return "Add a LinkedIn account or Company Page";
            }
        } else if (sourceStage === AddSourceWizardStage.EditOptions) {
            switch (sourceType) {
                case SourceType.Web:
                    return "Name this publication";
                case SourceType.PodcastRssFeed:
                    return "Name this podcast";
                case SourceType.Youtube:
                    return "Name this YouTube channel";
                case SourceType.Linkedin:
                    return "Name this LinkedIn property";
            }
        } else if (sourceStage === AddSourceWizardStage.Completed) {
            return "";
        } else if (sourceStage === AddSourceWizardStage.ExistsForm) {
            return "Report an issue";
        } else if (sourceStage === AddSourceWizardStage.AlreadyExists) {
            return "Did something go wrong?";
        } else if (sourceStage === AddSourceWizardStage.CantFindArticles) {
            return "I can't find any previously imported items";
        }
    }

    if (mainModalStage === UnifiedAddContentStages.AddManual) {
        if (addContentStage === AddContentWizardStage.AddContentImport) {
            switch (importPathTaken) {
                case ManualImportPathTaken.PodcastEpisode:
                    return "List the links to your podcast episodes";
                case ManualImportPathTaken.VideosOther:
                case ManualImportPathTaken.YoutubeOther:
                    return "List the links to videos you'd like to import";
                case ManualImportPathTaken.SocialMedia:
                    return "List the links to posts you'd like to import";
                case ManualImportPathTaken.PDFs:
                    return "List the links to PDFs you'd like to upload";
                case ManualImportPathTaken.Other:
                    return "Let's add some content to your account";
                case ManualImportPathTaken.AddSourceNonBylinedContent:
                    return "Import non-bylined articles";
                case ManualImportPathTaken.AddSourceSomethingElse:
                    return "If the automagic import doesn't work...";
                default:
                    return `List the links to your articles`;
            }
        } else if (addContentStage === AddContentWizardStage.AddContentProcess) {
            if (isCheckingDuplicate) return "Possible duplicate detected";
            return isProcessingItem ? "Edit preview" : "Importing content";
        } else if (addContentStage === AddContentWizardStage.AddContentComplete) {
            return "Import complete";
        }
    }

    if (mainModalStage === UnifiedAddContentStages.ContentUpload) {
        switch (fileUploadTarget) {
            case FileUploadTarget.PDF:
                return "Let's upload your PDFs";
            case FileUploadTarget.Email:
                return "Let's upload your emails";
        }
    }

    if (mainModalStage === UnifiedAddContentStages.WebsiteAddURL) {
        return "Let's capture your website";
    }

    if (mainModalStage === UnifiedAddContentStages.AddContentEmail) {
        return "Select a source";
    }

    if (mainModalStage === UnifiedAddContentStages.AddContentEmailFinished) {
        return "Copy secret email address";
    }

    if (mainModalStage === UnifiedAddContentStages.AutoUpdate) {
        return "Auto-update settings";
    }

    if (mainModalStage === UnifiedAddContentStages.CollectionManualBulkAdd) {
        return "Add from archive";
    }

    return "Add Content";
};

export const getPaywallModalTitle = (mainModalStage: UnifiedAddContentStages) => {
    switch (mainModalStage) {
        case UnifiedAddContentStages.ArticleOptions:
        case UnifiedAddContentStages.PodcastOptions:
        case UnifiedAddContentStages.YouTubeOptions:
            return "Plan upgrade required";
        default:
            return `Plan limit hit`;
    }
};

export const getPaywallModalDescription = (mainModalStage: UnifiedAddContentStages, subscriptionPlanKey: SubscriptionPlanKey | null) => {
    const planLabel = getPlanTierLabel(subscriptionPlanKey);

    switch (mainModalStage) {
        case UnifiedAddContentStages.ArticleOptions:
        case UnifiedAddContentStages.PodcastOptions:
        case UnifiedAddContentStages.YouTubeOptions:
            return `You are currently on our ${planLabel} plan. The option to automatically import your content from a specific website is part of our Standard and Pro plan. You can upgrade instantly below.`;
        default:
            return (
                <V3BodyGrey48>
                    To be able to add content, you'll need to either delete existing content items or upgrade your account.{" "}
                    <LearnMoreCTA
                        href="https://authory.com/help/en/articles/10091072-how-does-the-limit-of-10-content-items-work-for-the-free-plan"
                        text="Learn more about the free plan limit"
                    />
                </V3BodyGrey48>
            );
    }
};

export const getMaxLimitUsageMessage = (limit: number, used: number) => (
    <V3BodyGrey48>
        Your plan includes up to {formatMMSS(limit)} of audio. You’ve used {formatMMSS(used)}, and this upload would exceed your limit. To add more
        audios, delete existing ones or upgrade your plan.{" "}
        <LearnMoreCTA href="https://authory.com/help/en/articles/10091072-how-does-the-limit-of-10-content-items-work-for-the-free-plan" />
    </V3BodyGrey48>
);
